import { Injectable } from '@angular/core';
import { applyTransaction } from '@datorama/akita';
import { UserTrackingService } from '@y42/core/user-tracking/src';
import { environment } from '@y42/environment';
import { ErrorHandlerService } from '@y42/error-handling';
import { OrgUIModel } from '@y42/org/models';
import { OrgQuery } from '@y42/org/state';
import { ToastService } from '@y42/toast';
import { assertNonNullable } from '@y42/util/assert';
import { finalize, map, Observable, of, tap } from 'rxjs';
import { OrgSpaceStoreModel, OrgSpaceUpdateSettings } from '../models';
import { DataWarehouseConfig } from '../models/org-space-http.model';
import { OrgSpaceSelectedTrackingEvent } from '../models/org-space-tracking-events.model';
import { OrgSpaceQuery, OrgSpaceStore } from '../state';
import { OrgSpaceHttpService } from './org-space-http.service';

export interface GitProviderUrlParts {
  branch?: string;
  filePath?: string;
}

@Injectable({ providedIn: 'root' })
export class OrgSpaceService {
  constructor(
    private readonly orgSpaceHttpService: OrgSpaceHttpService,
    private readonly orgQuery: OrgQuery,
    private readonly orgSpaceStore: OrgSpaceStore,
    private readonly orgSpaceQuery: OrgSpaceQuery,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly toastService: ToastService,
    private readonly userTrackingService: UserTrackingService,
  ) {}

  setActiveOrgSpace(spaceId: OrgSpaceStoreModel['id']): void {
    const space = this.orgSpaceQuery.getEntity(spaceId);

    if (!space) {
      console.debug(`[OrgSpaceService]: space id '${spaceId}' not found`);
      return;
    }

    if (space.id === this.orgSpaceQuery.getActiveId()) {
      return;
    }

    console.debug(`[OrgSpaceService]: active space '${space.name}'`);
    this.orgSpaceStore.setActive(space.id);
    this.userTrackingService.track(new OrgSpaceSelectedTrackingEvent());
  }

  getGitInternalCloneUrl(spaceId: OrgSpaceStoreModel['id']): Observable<string> {
    return this.orgQuery.activeOrg$.pipe(
      map((org) => this.orgSpaceHttpService.getGitInternalCloneUrl(org.id, spaceId)),
    );
  }

  getGitProviderPublicRepositoryUrl(
    spaceId: OrgSpaceStoreModel['id'],
    { branch, filePath }: GitProviderUrlParts = {},
  ): string {
    const space = this.orgSpaceQuery.getEntity(spaceId);

    if (!space) {
      return '';
    }

    let url = `${environment.gitlab.url}/${space.repoFullName}`;

    if (branch) {
      url = `${url}/tree/${branch}`;

      if (filePath) {
        url = `${url}/${filePath}`;
      }
    }

    return url;
  }

  getGitLocalRepositoryPath(spaceId: OrgSpaceStoreModel['id']): string {
    const org = this.orgQuery.getActive();
    const space = this.orgSpaceQuery.getEntity(spaceId);
    return org && space ? `${org.slug}/${space.repoName}` : '';
  }

  createOrgSpace(
    orgId: OrgUIModel['id'],
    spaceName: string,
    dataWarehouseConfig: DataWarehouseConfig,
  ): Observable<OrgSpaceStoreModel | null> {
    // Do not update the store at this point, otherwise the query will emit before checking the acl permissions
    return this.orgSpaceHttpService.createOrgSpace(orgId, spaceName, dataWarehouseConfig).pipe(
      this.errorHandlerService.catchError('[OrgSpaceService.createOrgSpace]: could not create space', () => of(null), {
        toast: (error) => (error as Error).message || 'Could not create space',
      }),
    );
  }

  getOrgSpaces(orgId: OrgUIModel['id'], forceFetch?: boolean): Observable<OrgSpaceStoreModel[]> {
    if (this.orgSpaceQuery.areOrgSpacesLoaded(orgId) && !forceFetch) {
      return of(this.orgSpaceQuery.getOrgSpaces(orgId));
    }

    this.orgSpaceStore.setLoading(true);

    return this.orgSpaceHttpService.getOrgSpaces(orgId).pipe(
      tap((spaces) => {
        applyTransaction(() => {
          this.orgSpaceStore.upsertMany(spaces);
          this.orgSpaceStore.upsertOrgSpacesMap(orgId, spaces);
        });
      }),
      // Return sorted spaces
      map(() => this.orgSpaceQuery.getOrgSpaces(orgId)),
      finalize(() => {
        this.orgSpaceStore.setLoading(false);
      }),
      this.errorHandlerService.catchError('[OrgSpaceService]: failed to get spaces', () => of([]), {
        toast: (error) => (error as Error).message,
        context: {
          orgId,
        },
      }),
    );
  }

  deleteOrgSpace(orgId: OrgUIModel['id'], spaceId: OrgSpaceStoreModel['id']): Observable<boolean> {
    const space = this.orgSpaceQuery.getEntity(spaceId);
    assertNonNullable(space);

    return this.orgSpaceHttpService.deleteOrgSpace(orgId, spaceId).pipe(
      tap(() => {
        applyTransaction(() => {
          this.orgSpaceStore.remove(spaceId);
          this.orgSpaceStore.removeOrgSpacesMap(orgId, spaceId);
        });
      }),
      map(() => true),
      tap(() => {
        this.toastService.success(`Space ${space.name} deleted successfully`);
      }),
      this.errorHandlerService.catchError(
        `[OrgSpaceService.deleteOrgSpace]: failed to delete space "${spaceId}"`,
        () => of(false),
        {
          toast: (error: unknown) => (error as Error).message,
          context: {
            orgId,
            spaceId,
          },
        },
      ),
    );
  }

  update(
    orgId: OrgUIModel['id'],
    spaceId: OrgSpaceStoreModel['id'],
    settings: OrgSpaceUpdateSettings,
  ): Observable<boolean> {
    const space = this.orgSpaceQuery.getEntity(spaceId);

    if (!space) {
      return of(false);
    }

    return this.orgSpaceHttpService
      .update(orgId, spaceId, {
        ...settings,
        name: settings.name === space.name ? undefined : settings.name,
      })
      .pipe(
        tap(() => this.orgSpaceStore.update(spaceId, settings)),
        map(() => true),
        this.errorHandlerService.catchError(
          `[OrgSpaceService.updateOrgSpace]: failed to update space "${spaceId}"`,
          () => of(false),
          {
            toast: (error: unknown) => (error as Error).message,
            context: {
              orgId,
              spaceId,
            },
          },
        ),
      );
  }
}
